import { useState } from "react";

import { FormularioRegra } from "./FormularioRegra";
import { TabelaRegras } from "./TabelaRegras";
import {
  useCriarRegra,
  useEditarRegra,
  useRegras,
} from "../../lib/config-alertas/hooks";
import type {
  Regra,
  RegraIn,
  RegraPatch,
} from "../../lib/config-alertas/tipos";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seção de Regras da Tela 6 (FE-6b). Orquestra (espelha `SecaoCanais`):
 *
 *  - `useRegras` (lista) + `useCriarRegra`/`useEditarRegra` (mutations);
 *  - `form: FormAberto` = qual formulário está aberto (criar | editar | nenhum);
 *  - "Nova regra" gated por `configurar_alertas`;
 *  - `<TabelaRegras>` + `<FormularioRegra>` (quando aberto);
 *  - no `onSuccess` da mutação o form fecha; o hook já invalida a lista (refetch).
 *
 * 🔴 `key` ESTÁVEL POR ALVO no `<FormularioRegra>` (`id` da regra em edição,
 * "nova-regra" na criação): força REMOUNT ao trocar de alvo (lição FE-6a — sem isso
 * o estado do form A vazaria para o form B em Editar-A → Editar-B).
 *
 * SÓ tokens CPMH. Loading/erro como `SecaoCanais`/`PaginaBancos`.
 */

const ACAO_CONFIGURAR_ALERTAS = "configurar_alertas";

/** Qual formulário está aberto na seção. `null` = nenhum. */
type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; regra: Regra }
  | null;

export function SecaoRegras(): JSX.Element {
  const consulta = useRegras();
  const criar = useCriarRegra();
  const editar = useEditarRegra();
  const podeConfigurar = useTemPermissao(ACAO_CONFIGURAR_ALERTAS);

  const [form, setForm] = useState<FormAberto>(null);

  function salvarCriacao(corpo: RegraIn): void {
    criar.mutate(corpo, { onSuccess: () => setForm(null) });
  }

  function salvarEdicao(regra: Regra, corpo: RegraPatch): void {
    editar.mutate(
      { id: regra.id, corpo },
      { onSuccess: () => setForm(null) },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">Carregando regras…</p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as regras.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const regras = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      {podeConfigurar && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Nova regra
          </button>
        </div>
      )}

      <TabelaRegras
        regras={regras}
        aoEditar={(regra) => setForm({ tipo: "editar", regra })}
      />

      {form?.tipo === "criar" && (
        <FormularioRegra
          key="nova-regra"
          aoCriar={salvarCriacao}
          aoEditar={() => undefined}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioRegra
          key={form.regra.id}
          regra={form.regra}
          aoCriar={() => undefined}
          aoEditar={(corpo) => salvarEdicao(form.regra, corpo)}
          aoCancelar={() => setForm(null)}
          salvando={editar.isPending}
          erro={editar.error}
        />
      )}
    </div>
  );
}
